/**
 * EmailEditorTab: AI tools > Email editor admin view.
 *
 * How the fidelity editor (the second model call over every email draft)
 * is doing, as counts over a 7 / 30 / 90 day window:
 *   1. Totals: drafts through the editor, edited, served unedited and why,
 *      cuts and claims per draft, the editor's mean seconds.
 *   2. By day.
 *   3. What it cuts, by kind.
 *   4. By care manager.
 *
 * Counts only, by design: the words the editor cut are email text and stay
 * on the audit rows, which admin screens never read.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import {
  CUT_KIND_LABELS,
  cutKindLabel,
  editorErrorLabel,
  emailEditorApi,
  uneditedReasonLabel,
  WINDOW_OPTIONS,
  type EmailEditorReport,
  type WindowDays,
} from '@/lib/emailEditorReport';
import { formatRecordCount } from '@/lib/format';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';

export interface EmailEditorTabProps {
  tenantSlug: string;
}

function personLabel(display: string | null, email: string | null, userId: string): string {
  if (display && display.trim().length > 0) return display.trim();
  if (email && email.trim().length > 0) return email.trim();
  return `${userId.slice(0, 8)}…`;
}

/** Cuts and claims exist only on edited drafts, so that is the denominator. */
function perEdited(total: number, edited: number): string {
  if (edited === 0) return '–';
  return (total / edited).toFixed(1);
}

function seconds(ms: number | null): string {
  if (ms === null) return '–';
  return `${(ms / 1000).toFixed(0)} s`;
}

function share(n: number, of: number): string {
  if (of === 0) return '–';
  return `${Math.round((n / of) * 100)}%`;
}

const TH = 'px-3 py-2 font-medium';
const TH_R = 'px-3 py-2 font-medium text-right whitespace-nowrap';
const TD = 'px-3 py-2 text-ink';
const TD_R = 'px-3 py-2 text-right tabular-nums text-ink-soft';

export function EmailEditorTab({ tenantSlug }: EmailEditorTabProps): ReactNode {
  const [windowDays, setWindowDays] = useState<WindowDays>(30);

  const query = useQuery<EmailEditorReport, Error>({
    queryKey: ['admin-review', 'email-editor', tenantSlug, windowDays],
    queryFn: ({ signal }) => emailEditorApi.report(tenantSlug, windowDays, signal),
    staleTime: 30_000,
  });

  const report = query.data;
  const totals = report?.totals;
  const empty = !!report && totals!.drafts === 0 && totals!.editor_ok_rows === 0 && Object.keys(totals!.editor_error_rows).length === 0;
  const uneditedReasons = totals ? Object.entries(totals.unedited_by_error).sort((a, b) => b[1] - a[1]) : [];
  const editorErrors = totals ? Object.entries(totals.editor_error_rows).sort((a, b) => b[1] - a[1]) : [];
  const cutTotal = report ? Object.values(report.cut_kinds).reduce((a, b) => a + b, 0) : 0;
  const cutRows = report
    ? [
        ...CUT_KIND_LABELS.map(([kind, label]) => ({ kind, label, n: report.cut_kinds[kind] ?? 0 })),
        ...Object.keys(report.cut_kinds)
          .filter((k) => !CUT_KIND_LABELS.some(([kind]) => kind === k))
          .map((kind) => ({ kind, label: cutKindLabel(kind), n: report.cut_kinds[kind] ?? 0 })),
      ].filter((r) => r.n > 0)
    : [];

  return (
    <div data-testid="email-editor-tab">
      <div role="group" aria-label="Time window" className="flex gap-1.5 mb-4 items-center">
        <span className="text-xs text-ink-soft">Window:</span>
        {WINDOW_OPTIONS.map((option) => (
          <Button
            key={option}
            variant={windowDays === option ? 'primary' : 'secondary'}
            size="sm"
            aria-pressed={windowDays === option}
            onClick={() => setWindowDays(option)}
          >
            {`${option} days`}
          </Button>
        ))}
      </div>

      {query.isLoading && (
        <div>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="the email editor report" />}

      {query.isSuccess && empty && (
        <div className="px-3 py-8 text-center bg-sage-50 border border-sage-100 rounded-card text-sm text-ink">
          Once drafts go through the editor, you'll see them here.
        </div>
      )}

      {query.isSuccess && report && totals && !empty && (
        <div className="flex flex-col gap-6">
          <section data-testid="email-editor-totals">
            <h3 className="text-sm font-semibold text-ink mb-2">Over {report.window_days} days</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat label="Drafts through the editor" value={formatRecordCount(totals.drafts)} />
              <Stat label="Edited" value={`${formatRecordCount(totals.applied)} (${share(totals.applied, totals.drafts)})`} />
              <Stat label="Served unedited" value={formatRecordCount(totals.unedited)} tone={totals.unedited > 0 ? 'warn' : 'plain'} />
              <Stat label="Cuts per edited draft" value={perEdited(totals.changes_total, totals.applied)} />
              <Stat label="Claims checked per edited draft" value={perEdited(totals.claims_total, totals.applied)} />
              <Stat label="Editor call, mean" value={seconds(totals.mean_duration_ms)} />
            </div>
            {(uneditedReasons.length > 0 || editorErrors.length > 0) && (
              <div className="mt-3 text-xs text-ink-soft" data-testid="email-editor-failures">
                {uneditedReasons.length > 0 && (
                  <p>
                    Served unedited because:{' '}
                    {uneditedReasons.map(([code, n]) => `${uneditedReasonLabel(code)} (${n})`).join('; ')}.
                  </p>
                )}
                {editorErrors.length > 0 && (
                  <p>
                    Editor calls that failed, retries included:{' '}
                    {editorErrors.map(([code, n]) => `${editorErrorLabel(code)} (${n})`).join('; ')}.
                  </p>
                )}
              </div>
            )}
          </section>

          {report.by_day.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-ink mb-2">By day</h3>
              <div className="overflow-x-auto bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
                <table className="w-full text-sm" data-testid="email-editor-by-day">
                  <thead>
                    <tr className="border-b border-sage-800/[8%] text-left text-xs text-ink-soft">
                      <th className={TH}>Day</th>
                      <th className={TH_R}>Drafts</th>
                      <th className={TH_R}>Edited</th>
                      <th className={TH_R}>Unedited</th>
                      <th className={TH_R}>Cuts per edited draft</th>
                      <th className={TH_R}>Claims per edited draft</th>
                      <th className={TH_R}>Editor call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_day.map((d) => (
                      <tr key={d.day} className="border-b border-sage-800/[5%] last:border-0">
                        <td className={TD}>{d.day}</td>
                        <td className={TD_R}>{formatRecordCount(d.drafts)}</td>
                        <td className={TD_R}>{formatRecordCount(d.applied)}</td>
                        <td className={TD_R}>{formatRecordCount(d.unedited)}</td>
                        <td className={TD_R}>{perEdited(d.changes_total, d.applied)}</td>
                        <td className={TD_R}>{perEdited(d.claims_total, d.applied)}</td>
                        <td className={TD_R}>{seconds(d.mean_duration_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {cutRows.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-ink mb-2">What it cuts</h3>
              <div className="overflow-x-auto bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
                <table className="w-full text-sm" data-testid="email-editor-cut-kinds">
                  <thead>
                    <tr className="border-b border-sage-800/[8%] text-left text-xs text-ink-soft">
                      <th className={TH}>Kind of cut</th>
                      <th className={TH_R}>Cuts</th>
                      <th className={TH_R}>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cutRows.map((r) => (
                      <tr key={r.kind} className="border-b border-sage-800/[5%] last:border-0">
                        <td className={TD}>{r.label}</td>
                        <td className={TD_R}>{formatRecordCount(r.n)}</td>
                        <td className={TD_R}>{share(r.n, cutTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {report.by_user.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-ink mb-2">By care manager</h3>
              <div className="overflow-x-auto bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
                <table className="w-full text-sm" data-testid="email-editor-by-user">
                  <thead>
                    <tr className="border-b border-sage-800/[8%] text-left text-xs text-ink-soft">
                      <th className={TH}>Person</th>
                      <th className={TH_R}>Drafts</th>
                      <th className={TH_R}>Edited</th>
                      <th className={TH_R}>Unedited</th>
                      <th className={TH_R}>Cuts per edited draft</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_user.map((u) => (
                      <tr key={u.user_id} className="border-b border-sage-800/[5%] last:border-0">
                        <td className={TD}>{personLabel(u.user_display_name, u.user_email, u.user_id)}</td>
                        <td className={TD_R}>{formatRecordCount(u.drafts)}</td>
                        <td className={TD_R}>{formatRecordCount(u.applied)}</td>
                        <td className={TD_R}>{formatRecordCount(u.unedited)}</td>
                        <td className={TD_R}>{perEdited(u.changes_total, u.applied)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <p className="text-xs text-ink-soft">
            Counts only. What the editor cut, word for word, stays on the audit rows; days are {report.time_zone} calendar days.
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'plain' }: { label: string; value: string; tone?: 'plain' | 'warn' }): ReactNode {
  return (
    <div className="bg-paper border border-sage-800/[8%] rounded-card px-3 py-2">
      <div className="text-xs text-ink-soft">{label}</div>
      <div className={'text-lg tabular-nums ' + (tone === 'warn' ? 'text-pepper' : 'text-ink')}>{value}</div>
    </div>
  );
}
